import { useState } from 'react';
import { 
    Plus, 
    Minus,
    X,
    Search,
    Repeat,
    Hash
} from 'lucide-react';
import { Button } from '@mui/material';

type AddConditioningCircuitProps = {
    circuitHolder: any;
    userCircuits: any[];
    handleSubmit: (circuit: any) => void;
    handleCancel: () => void;
};

const AddConditioningCircuit: React.FC<AddConditioningCircuitProps> = ({ 
    circuitHolder, 
    userCircuits, 
    handleSubmit, 
    handleCancel 
}) => {
    const [circuit, setCircuit] = useState({
        ...circuitHolder,
        circuit_id: userCircuits[0]?.id || null,
        title: userCircuits[0]?.title || '',
        sets: circuitHolder.sets || 1
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    const filteredCircuits = userCircuits.filter(c => 
        c.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const currentCircuit = userCircuits.find(c => c.id === circuit.circuit_id);

    const handleCircuitSelect = (selectedCircuit: any) => {
        setCircuit({
            ...circuit,
            circuit_id: selectedCircuit.id,
            title: selectedCircuit.title
        });
        setSearchTerm(selectedCircuit.title);
        setShowDropdown(false);
    };

    const handleSetsChange = (newSets: number) => {
        setCircuit({
            ...circuit,
            sets: Math.max(1, newSets)
        });
    };

    return (
        <div className="fixed inset-0 bg-black-500 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-6 text-black relative">
                    <div className="absolute inset-0 bg-white opacity-10"></div>
                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Plus className="w-6 h-6" />
                            <h2 className="text-xl font-bold">Add Circuit</h2>
                        </div>
                        <button
                            onClick={handleCancel}
                            className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Circuit Selection */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Circuit</label>
                        <div className="relative">
                            <div className="relative">
                                <Search className="w-5 h-5 text-gray-400 absolute left-1 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="text"
                                    value={searchTerm || currentCircuit?.title || ''}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setShowDropdown(true);
                                    }}
                                    onFocus={() => setShowDropdown(true)}
                                    placeholder="Search or select a circuit"
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                                    style={{ fontSize: '1rem' }}
                                />
                            </div>
                            
                            {showDropdown && filteredCircuits.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {filteredCircuits.map((c) => (
                                        <button
                                            key={c.id}
                                            onClick={() => handleCircuitSelect(c)}
                                            className="w-full px-4 py-3 text-left hover:bg-teal-50 focus:bg-teal-50 outline-none border-b border-gray-100 last:border-b-0"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <Repeat className="w-4 h-4 text-teal-500" />
                                                <span style={{ fontSize: '1rem' }}>{c.title}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sets Section */}
                    <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-3">
                            <Hash className="w-5 h-5 text-blue-500" />
                            <span className="text-base font-medium text-blue-800">Sets</span>
                        </div>
                        
                        <div className="flex items-center justify-center space-x-4">
                            <button
                                onClick={() => handleSetsChange(circuit.sets - 1)}
                                className="w-10 h-10 bg-blue-200 hover:bg-blue-300 rounded-full flex items-center justify-center transition-colors"
                            >
                                <Minus className="w-4 h-4 text-blue-700" />
                            </button>
                            <span className="text-2xl font-bold text-blue-800 min-w-[3rem] text-center">
                                {circuit.sets}
                            </span>
                            <button
                                onClick={() => handleSetsChange(circuit.sets + 1)}
                                className="w-10 h-10 bg-blue-200 hover:bg-blue-300 rounded-full flex items-center justify-center transition-colors"
                            >
                                <Plus className="w-4 h-4 text-blue-700" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className='flex-buttons'>
                    <Button
                        style={{margin: "10px 5px 5px", width: '100px'}}
                        disabled={circuit.circuit_id === null}
                        onClick={() => handleSubmit(circuit)}
                        variant="contained"
                    >
                        Save
                    </Button>

                    <Button
                        style={{margin: "10px 5px 5px", width: '100px'}}
                        onClick={handleCancel}
                        variant="contained"
                    >
                        Cancel
                    </Button>
                </div> 
            </div>
        </div>
    );
};

export default AddConditioningCircuit;